/*!
 * textStyles.js — style(): a text's look and arrival, named once, used by name
 * © 2026 Rob Canning — GPLv3
 *
 *     style(agent, "font-size:32px; color:black; text-align:left; max-width:28vw;
 *       overflow-wrap:anywhere; text-shadow:none; pointer-events:none",
 *       zoom:12, blur:50, fade:1500, dur:2)
 *     text(uid:a1, cycle:"lines.csv", col:"text", say:1, at:[0.2, 0.25], style:agent)
 *     text(uid:a3, ..., style:"agent; font-size:40px")   the preset, then changes
 *     text(uid:a2, ..., style:agent, dur:4)              a text's own setting wins
 *     style(agent, "font-size:28px")                     the look of every text
 *                                                        using it changes at once
 *     style(agent)                                       what it is (the console)
 *     style(agent, off)                                  gone
 *
 * A preset is css and any of text()'s own settings (zoom, blur, fade, dur,
 * gap, say ...). When a text runs (cueDispatcher, before its cue), the
 * settings it does not give itself are taken from its preset, and its look
 * becomes a stylesheet rule for its uid -- the preset's css, then the line's
 * own after it -- so running style() again restyles the words on the screen
 * now and to come, while the settings change from the text's next run.
 * Several presets: style:"agent; big" -- later ones over earlier.
 */

import { onReset } from "../system/teardown.js";

const presets = new Map();          // name -> { css, args: [{ type, value }] }
const looks = new Map();            // text uid -> { names: [preset names], own: css of its own line }
let sheet = null;

/** "a: b; c: d" -> [["a", "b"], ["c", "d"]] */
function declarations(css) {
  return String(css || "").split(";").map(d => d.trim()).filter(Boolean).map(d => {
    const i = d.indexOf(":");
    return i > 0 ? [d.slice(0, i).trim(), d.slice(i + 1).trim().replace(/\s*!important\s*$/i, "")] : null;
  }).filter(Boolean);
}

/** A style: value split into preset names (known ones) and css of its own. */
export function splitStyle(value) {
  const names = [], own = [];
  const parts = Array.isArray(value) ? value.map(String) : String(value ?? "").split(";");
  for (const p of parts.map(s => s.trim()).filter(Boolean)) {
    if (!p.includes(":") && presets.has(p)) names.push(p);
    else if (p.includes(":")) own.push(p);
  }
  return { names, own: own.join("; ") };
}

/** The css a text wears: its presets' in order, then its own line's. */
export function cssFor(names, own) {
  const decl = new Map();
  for (const n of names) for (const [k, v] of declarations(presets.get(n)?.css)) decl.set(k, v);
  for (const [k, v] of declarations(own)) decl.set(k, v);
  return [...decl].map(([k, v]) => `${k}: ${v}`).join("; ");
}

// style() presets and the texts wearing them are the project's
onReset("project", function forgetStyles() {
  presets.clear();
  looks.clear();
  if (sheet) writeSheet();
});

/** The stylesheet: a rule for each text that uses a preset. */
function writeSheet() {
  if (typeof document === "undefined") return;
  if (!sheet) {
    sheet = document.createElement("style");
    sheet.id = "oscilla-text-styles";
    document.head.appendChild(sheet);
  }
  let out = "";
  for (const [uid, { names, own }] of looks) {
    const css = cssFor(names, own);
    if (!css) continue;
    // over the cue's inline defaults: important, as an inline style is otherwise first
    const body = declarations(css).map(([k, v]) => `${k}: ${v} !important;`).join(" ");
    // the words: an overlay's own, or the inner of a scroll / ticker box (its style: goes there too)
    const u = CSS.escape(uid);
    out += `.cue-text-overlay[data-uid="${u}"], [data-uid="${u}"] > .cue-text-scroll-inner, [data-uid="${u}"] > .cue-text-ticker-inner { ${body} }\n`;
  }
  sheet.textContent = out;
}

/**
 * A text cue with style: naming presets: the settings it does not give
 * itself taken from them, its look a rule for its uid (and the style: arg
 * taken off the line, so the words wear the rule). Null when it names none.
 */
export function expandStyle(ast) {
  if (ast?.type !== "cueText" || !presets.size) return null;
  const styleArg = ast.args?.find(a => a.type === "style");
  if (!styleArg) return null;
  const { names, own } = splitStyle(styleArg.value);
  if (!names.length) return null;
  const given = new Set(ast.args.map(a => a.type));
  const args = ast.args.filter(a => a.type !== "style");
  // later presets over earlier ones; the line's own over all
  const taken = new Map();
  for (const n of names) for (const a of presets.get(n).args) taken.set(a.type, a);
  for (const [k, a] of taken) if (!given.has(k)) args.push(a);
  const uid = String(ast.args.find(a => a.type === "uid")?.value ?? "");
  if (uid) {
    looks.set(uid, { names, own });
    writeSheet();
  } else if (own || names.length) {
    // no uid to hang a rule on: the css goes on the line, as written
    args.push({ type: "style", value: cssFor(names, own) });
  }
  return { ...ast, args };
}

/** style(...) as a cue. */
export function handleStyleCue(ast) {
  const arg = k => ast?.args?.find(a => a.type === k)?.value;
  const name = String(arg("uid") ?? "");
  if (!name) { console.warn('[style] style(agent, "font-size:32px; ...", zoom:12, fade:1500)'); return; }
  if (arg("off") != null) {
    presets.delete(name);
    for (const [uid, l] of [...looks]) {
      l.names = l.names.filter(n => n !== name);
      if (!l.names.length && !l.own) looks.delete(uid);
    }
    writeSheet();
    return;
  }
  const settings = (ast.args || []).filter(a => !["uid", "css", "off"].includes(a.type));
  if (arg("css") == null && !settings.length) {
    const p = presets.get(name);
    console.info(`[style] ${name}: ${p ? `"${p.css}" ${p.args.map(a => `${a.type}:${JSON.stringify(a.value)}`).join(", ")}` : "no such style"}`);
    return;
  }
  const was = presets.get(name);
  // what is given changes, the rest is kept: style(agent, "font-size:28px")
  // only resizes (style(agent, off) and again to start afresh)
  const args = new Map((was?.args || []).map(a => [a.type, a]));
  for (const a of settings) args.set(a.type, a);
  const css = arg("css") != null ? cssFor([], `${was?.css ?? ""}; ${arg("css")}`) : was?.css ?? "";
  presets.set(name, { css, args: [...args.values()] });
  writeSheet();          // the texts wearing it: at once
}

export const styleNames = () => [...presets.keys()];
